import { t } from '@hefe/i18n';
import {
  BasketballPracticeTimesScheduleOverviewTeam,
  PracticeTimesScheduleEntry,
  ResponseBasketballGetPracticeTimeScheduleOverview,
} from '@hefe/models';
import { colors } from '@hefe/ui';
import { Col, Descriptions, Row, Table } from 'antd';
import PracticeTimesScheduleOverviewNewTime from '../practice-times-schedule-overview-new-time/practice-times-schedule-overview-new-time.component';
import PracticeTimesScheduleOverviewTime from '../practice-times-schedule-overview-time/practice-times-schedule-overview-time.component';
import styles from './practice-times-overview.module.scss';

interface PracticeTimesScheduleOverviewProps {
  data?: ResponseBasketballGetPracticeTimeScheduleOverview;
  activeScheduleId: number;
  loading: boolean;
  disableEditing?: boolean;

  onDelete?: (time: PracticeTimesScheduleEntry) => void;
  isDeleting?: boolean;

  onOpenSettings?: (
    teamId: number,
    practiceTime: PracticeTimesScheduleEntry | undefined,
    dayOfWeek: number
  ) => void;
}

export const PracticeTimesScheduleOverview = (
  props: PracticeTimesScheduleOverviewProps
) => {
  const {
    data,
    activeScheduleId,
    loading,
    disableEditing,
    onDelete,
    isDeleting,
    onOpenSettings,
  } = props;

  const renderDay = (
    team: BasketballPracticeTimesScheduleOverviewTeam,
    day: number
  ) => {
    let practiceTimesComponents = team?.entries
      ?.filter((time: PracticeTimesScheduleEntry) => time.weekday === day)
      .map((time: PracticeTimesScheduleEntry) => (
        <PracticeTimesScheduleOverviewTime
          key={time.id}
          team={team}
          time={time}
          gyms={data?.gyms ?? []}
          dayOfWeek={day}
          scheduleId={activeScheduleId}
          disableEditing={disableEditing}
          onDelete={onDelete}
          isDeleting={isDeleting}
          onOpenSettings={onOpenSettings}
        />
      ));

    if (practiceTimesComponents?.length === 0) {
      practiceTimesComponents = [
        <PracticeTimesScheduleOverviewNewTime
          key={`${team.id}-${day}`}
          team={team}
          dayOfWeek={day}
          disableEditing={disableEditing}
          onOpenSettings={onOpenSettings}
        />,
      ];
    }

    return {
      props: {
        style: {
          padding: 0,
        },
      },
      children: practiceTimesComponents,
    };
  };

  return (
    <Row>
      <Col span={24}>
        <Table
          dataSource={data?.teams}
          pagination={false}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
          columns={[
            {
              title: t('team'),
              dataIndex: 'name',
              key: 'team',
              fixed: 'left',
              sorter: (a, b) => a.name.localeCompare(b.name),
            },
            {
              title: t('weekdays.monday'),
              key: 'monday',
              width: 130,
              render: (team) => renderDay(team, 1),
            },
            {
              title: t('weekdays.tuesday'),
              key: 'tuesday',
              width: 130,
              render: (team) => renderDay(team, 2),
            },
            {
              title: t('weekdays.wednesday'),
              key: 'wednesday',
              width: 130,
              render: (team) => renderDay(team, 3),
            },
            {
              title: t('weekdays.thursday'),
              key: 'thursday',
              width: 130,
              render: (team) => renderDay(team, 4),
            },
            {
              title: t('weekdays.friday'),
              key: 'friday',
              width: 130,
              render: (team) => renderDay(team, 5),
            },
            {
              title: t('weekdays.saturday'),
              key: 'saturday',
              width: 130,
              render: (team) => renderDay(team, 6),
            },
            {
              title: t('weekdays.sunday'),
              key: 'sunday',
              width: 130,
              render: (team) => renderDay(team, 0),
            },
          ]}
        />
      </Col>

      {!loading && (
        <Col xs={24}>
          <Descriptions
            className={styles['legend']}
            title={t('legend')}
            bordered
            column={1}
          >
            {data?.gyms?.map((gym) => (
              <Descriptions.Item key={gym.id} label={gym.name}>
                <div
                  className={styles['legend-item']}
                  style={{ background: gym.color }}
                />
              </Descriptions.Item>
            ))}

            <Descriptions.Item
              key="split_location"
              label={t('practice_time.split_gym.title')}
            >
              <div
                className={styles['legend-item']}
                style={{
                  backgroundImage: `linear-gradient(to right, ${colors.primary} 50%, ${colors.primary}80 50%)`,
                  backgroundSize: '10px 100%',
                }}
              />
            </Descriptions.Item>
          </Descriptions>
        </Col>
      )}
    </Row>
  );
};
